import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useRef,
} from 'react';
import { calendarService } from '~/src/services/calendarService';
import type { AccountConnections, CalendarEvent } from '~/src/types/api';
import { withMicrosoftProvider } from '../helpers/calendarEventHelpers';

type CalendarProvider = 'google' | 'microsoft';

type UseCalendarAuthErrorRecoveryParams = {
  activeTab: string;
  refetchConnections: () => Promise<{ data?: AccountConnections }>;
  authErrorShownRef: MutableRefObject<{ google: boolean; microsoft: boolean }>;
  googleSyncTokenRef: MutableRefObject<Record<string, string | undefined>>;
  googleCacheRef: MutableRefObject<
    Record<string, { events: CalendarEvent[]; nextPageToken?: string }>
  >;
  microsoftDeltaLinkRef: MutableRefObject<Record<string, string | undefined>>;
  microsoftCacheRef: MutableRefObject<
    Record<string, { events: CalendarEvent[]; nextLink?: string }>
  >;
  setGoogleReconnectError: Dispatch<SetStateAction<string | null>>;
  setGoogleEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setGoogleNextPageToken: Dispatch<SetStateAction<string | undefined>>;
  setMicrosoftEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setMicrosoftNextLink: Dispatch<SetStateAction<string | undefined>>;
  onCalendarFetchAuthError?: (message: string) => void;
};

export const useCalendarAuthErrorRecovery = ({
  activeTab,
  refetchConnections,
  authErrorShownRef,
  googleSyncTokenRef,
  googleCacheRef,
  microsoftDeltaLinkRef,
  microsoftCacheRef,
  setGoogleReconnectError,
  setGoogleEvents,
  setGoogleNextPageToken,
  setMicrosoftEvents,
  setMicrosoftNextLink,
  onCalendarFetchAuthError,
}: UseCalendarAuthErrorRecoveryParams) => {
  const authRecoveryInProgressRef = useRef<{ google: boolean; microsoft: boolean }>({
    google: false,
    microsoft: false,
  });

  const persistAndBroadcastCalendarAuthError = useCallback(
    (provider: CalendarProvider, message: string) => {
      authErrorShownRef.current[provider] = true;
      if (provider === 'google') {
        setGoogleReconnectError(message);
      }
      try {
        sessionStorage.setItem(`calendarReconnectError:${provider}`, message);
      } catch {
        // Ignore storage access errors
      }
      window.dispatchEvent(
        new CustomEvent('calendar-fetch-auth-error', {
          detail: { provider, message },
        }),
      );
      onCalendarFetchAuthError?.(message);
    },
    [authErrorShownRef, onCalendarFetchAuthError, setGoogleReconnectError],
  );

  const reportCalendarAuthError = useCallback(
    (provider: CalendarProvider, message?: string) => {
      if (!message || authErrorShownRef.current[provider]) {
        return;
      }

      // Google access tokens can expire while this tab is idle. Try one silent
      // refresh path before surfacing reconnect guidance in the UI.
      if (provider === 'google' && !authRecoveryInProgressRef.current.google) {
        authRecoveryInProgressRef.current.google = true;

        void (async () => {
          try {
            const refreshResult = await refetchConnections();
            const refreshedGoogle = refreshResult.data?.google;
            const hasUsableToken =
              typeof refreshedGoogle?.login_link === 'string' &&
              refreshedGoogle.login_link.length > 0;

            if (refreshedGoogle?.connected && hasUsableToken) {
              const page = await calendarService.fetchGoogleCalendarEventsList(
                refreshedGoogle,
                activeTab,
              );

              if (!page.authError) {
                const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) => ({
                  ...event,
                  provider: 'google',
                }));

                authErrorShownRef.current.google = false;
                setGoogleReconnectError(null);
                setGoogleEvents(mappedEvents);
                setGoogleNextPageToken(page.nextPageToken);
                if (page.nextSyncToken) {
                  googleSyncTokenRef.current[activeTab] = page.nextSyncToken;
                }
                googleCacheRef.current[activeTab] = {
                  events: mappedEvents,
                  nextPageToken: page.nextPageToken,
                };

                sessionStorage.removeItem('calendarReconnectError:google');
                return;
              }
            }
          } catch {
            // Fall through to reconnect messaging.
          } finally {
            authRecoveryInProgressRef.current.google = false;
          }

          persistAndBroadcastCalendarAuthError(provider, message);
        })();
        return;
      }

      // Microsoft access tokens can also expire while idle. Mirror the same
      // silent refresh path used for Google before showing reconnect guidance.
      if (provider === 'microsoft' && !authRecoveryInProgressRef.current.microsoft) {
        authRecoveryInProgressRef.current.microsoft = true;

        void (async () => {
          try {
            const refreshResult = await refetchConnections();
            const refreshedMicrosoft = refreshResult.data?.microsoft;
            const hasUsableToken =
              typeof refreshedMicrosoft?.login_link === 'string' &&
              refreshedMicrosoft.login_link.length > 0;

            if (refreshedMicrosoft?.connected && hasUsableToken) {
              const page = await calendarService.fetchMicrosoftCalendarEventsList(
                refreshedMicrosoft,
                activeTab,
              );

              if (!page.authError) {
                const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) =>
                  withMicrosoftProvider(event),
                );

                authErrorShownRef.current.microsoft = false;
                setMicrosoftEvents(mappedEvents);
                setMicrosoftNextLink(page.nextLink);
                if (page.deltaLink) {
                  microsoftDeltaLinkRef.current[activeTab] = page.deltaLink;
                }
                microsoftCacheRef.current[activeTab] = {
                  events: mappedEvents,
                  nextLink: page.nextLink,
                };
                sessionStorage.removeItem('calendarReconnectError:microsoft');
                return;
              }
            }
          } catch {
            // Fall through to reconnect messaging.
          } finally {
            authRecoveryInProgressRef.current.microsoft = false;
          }

          persistAndBroadcastCalendarAuthError(provider, message);
        })();
        return;
      }

      persistAndBroadcastCalendarAuthError(provider, message);
    },
    [
      activeTab,
      authErrorShownRef,
      googleCacheRef,
      googleSyncTokenRef,
      microsoftCacheRef,
      microsoftDeltaLinkRef,
      persistAndBroadcastCalendarAuthError,
      refetchConnections,
      setGoogleEvents,
      setGoogleNextPageToken,
      setGoogleReconnectError,
      setMicrosoftEvents,
      setMicrosoftNextLink,
    ],
  );

  return {
    reportCalendarAuthError,
  };
};
